import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  FormField,
  Input,
} from '@/components/ui';
import { startImpersonation } from '@/api/admin';
import { describeApiError, getApiErrorCode } from '@/lib/api-errors';

/** Account the admin wants to work in. */
export interface ImpersonateTarget {
  id: string;
  email: string;
}

export interface ImpersonateDialogProps {
  /** The dialog is open while a target is set. */
  target: ImpersonateTarget | null;
  onClose: () => void;
}

/**
 * - `password`: asks for the admin's password.
 * - `pending`: request running; confirm is blocked.
 * - `manual`: the popup was blocked, the code waits behind a link.
 * - `expired`: the manual link outlived its code.
 */
type Step = 'password' | 'pending' | 'manual' | 'expired';

function impersonationUrl(code: string): string {
  // The code travels in the fragment, which never reaches a server or a
  // Referer header.
  return `/impersonate#code=${encodeURIComponent(code)}`;
}

/**
 * Confirms an impersonation with the admin's password and opens the target
 * account in a new tab. The tab is opened synchronously inside the click
 * (popup blockers only allow that) and navigated once the one-time code
 * arrives; a blocked popup falls back to a link that is valid for the
 * lifetime of the code.
 */
export function ImpersonateDialog({ target, onClose }: ImpersonateDialogProps) {
  const [step, setStep] = useState<Step>('password');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState<string | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Blocks a second request before the disabled button has rendered.
  const pendingRef = useRef(false);
  // Identifies the current dialog session; a response for an older one
  // only closes its window and no longer touches the dialog state.
  const sessionRef = useRef(0);

  const targetId = target?.id ?? null;

  useEffect(() => {
    sessionRef.current += 1;
    pendingRef.current = false;
    setStep('password');
    setPassword('');
    setError(null);
    setManualCode(null);
    setDeadline(null);
  }, [targetId]);

  useEffect(() => {
    if (step !== 'manual' || deadline === null) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) {
        setManualCode(null);
        setStep('expired');
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [step, deadline]);

  const handleConfirm = async () => {
    if (!target || pendingRef.current || !password) return;

    // Must stay synchronous in the click. Without `noopener`, because with
    // it `window.open` always returns null; the opener is cut right after.
    const win = window.open('about:blank', '_blank');
    if (win) win.opener = null;

    pendingRef.current = true;
    const session = sessionRef.current;
    setError(null);
    setStep('pending');

    try {
      const { code, expiresInSeconds } = await startImpersonation({
        targetUserId: target.id,
        password,
      });

      if (session !== sessionRef.current) {
        // The admin cancelled while the request ran: the tab must never
        // become a live session in the target account.
        win?.close();
        return;
      }

      if (win) {
        win.location.replace(impersonationUrl(code));
        onClose();
        return;
      }

      setPassword('');
      setManualCode(code);
      setDeadline(Date.now() + expiresInSeconds * 1000);
      setSecondsLeft(expiresInSeconds);
      setStep('manual');
    } catch (requestError) {
      win?.close();
      if (session !== sessionRef.current) return;
      if (getApiErrorCode(requestError) === 'impersonation_invalid_password') {
        setPassword('');
      }
      setError(describeApiError(requestError));
      setStep('password');
    } finally {
      if (session === sessionRef.current) pendingRef.current = false;
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleConfirm();
  };

  const restart = () => {
    setError(null);
    setStep('password');
  };

  const pending = step === 'pending';

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent closeLabel="Close">
        <DialogTitle>Log in as user</DialogTitle>
        <DialogDescription>
          You will work fully inside{' '}
          <span className="font-semibold text-ink">{target?.email}</span> in a
          new tab. Everything you do there happens in this account.
        </DialogDescription>

        {(step === 'password' || step === 'pending') && (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <FormField label="Your password" error={error ?? undefined}>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={pending}
                autoFocus
              />
            </FormField>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                loading={pending}
                loadingLabel="Starting…"
                disabled={!password}
              >
                Log in as user
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === 'manual' && manualCode && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink-2">
              Your browser blocked the new tab. Open it with the link below. The
              link is valid for one minute.
            </p>
            <p className="font-mono text-sm text-mute" aria-live="polite">
              Expires in {secondsLeft} s
            </p>
            <DialogFooter>
              <Button asChild>
                <a
                  href={impersonationUrl(manualCode)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                >
                  Open impersonation tab
                </a>
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'expired' && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink-2">
              The link has expired. Enter your password again to get a new one.
            </p>
            <DialogFooter>
              <Button type="button" onClick={restart}>
                Request a new code
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
